/**
 * Meta Pixel and Google Ads, with consent.
 *
 * The snippets in index.html load both tags and immediately withhold consent,
 * so nothing is sent to Meta or Google until the visitor agrees. Everything the
 * app reports goes through here, so there is one place to check that.
 */

import { warnQuietly } from "./api";

export const CONSENT_KEY = "tt-analytics-consent";
const GRANTED = "granted";
const DECLINED = "declined";

/**
 * The Google Consent Mode v2 signals we control. Ad storage covers the Google
 * Ads cookie itself; the two ad_* signals cover what may be sent on to Google
 * and whether it can personalise. index.html starts all of them denied.
 */
const GOOGLE_SIGNALS = [
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "analytics_storage",
];

const googleConsent = (granted) =>
  GOOGLE_SIGNALS.reduce(
    (signals, name) => ({ ...signals, [name]: granted ? "granted" : "denied" }),
    {},
  );

/** "granted", "declined", or null when they haven't chosen yet. */
export const readConsent = () => {
  try {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    return stored === GRANTED || stored === DECLINED ? stored : null;
  } catch (error) {
    // Private browsing with storage blocked: treat it as "not chosen yet", so
    // the notice shows again and nothing is sent until they agree.
    warnQuietly("could not read the cookie choice", error);
    return null;
  }
};

export const hasConsent = () => readConsent() === GRANTED;

const tellMeta = (...args) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function") {
    window.fbq(...args);
  }
};

const tellGoogle = (...args) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag(...args);
  }
};

/** Pass the visitor's choice to both tags. */
const applyConsent = (granted) => {
  tellMeta("consent", granted ? "grant" : "revoke");
  tellGoogle("consent", "update", googleConsent(granted));
};

/**
 * Record the visitor's choice and tell both tags about it.
 * Granting releases the page views queued since they arrived.
 */
export const setConsent = (granted) => {
  try {
    window.localStorage.setItem(CONSENT_KEY, granted ? GRANTED : DECLINED);
  } catch (error) {
    // Their choice is still honoured for this visit - the tags are told below
    // either way. It just won't be remembered next time, so the notice will
    // reappear. Worth seeing in the console while developing.
    warnQuietly("could not remember the cookie choice", error);
  }
  applyConsent(granted);
};

/** Re-apply a choice made on an earlier visit. Call once, on start-up. */
export const applyStoredConsent = () => {
  const stored = readConsent();
  if (stored) applyConsent(stored === GRANTED);
  return stored;
};

/** Standard or custom Meta event, only ever sent with consent. */
export const track = (event, params) => {
  if (!hasConsent()) return;
  // Only pass the second argument when there is one: fbq logs a warning for
  // an explicit undefined.
  if (params) tellMeta("track", event, params);
  else tellMeta("track", event);
};

/**
 * The Google Ads conversion action. One event name covers both a finished quote
 * and a contact request, because both are the same thing to the business: a
 * real enquiry worth paying for.
 */
const GOOGLE_CONVERSION_EVENT = "ads_conversion_Form_2";

/**
 * The Google Ads conversion actions, one per thing we care about. Each is a
 * conversion label inside the account loaded in index.html; `send_to` is what
 * tells Google which action a conversion belongs to.
 */
const ADS_ACCOUNT = "AW-18077264319";
const ADS_ACTIONS = {
  quoteCompleted: "tC7xCN-syJwdEL_T9KtD",
  contactRequest: "oFWhCJWgv5wdEL_T9KtD",
  financeEnquiry: "3wj4COOgjJkcEL_T9KtD",
};

/**
 * Report one named conversion action, only ever with consent.
 *
 * Anything else we know - the quoted figure, the reference - rides along, so
 * Google can value the conversion and discard a duplicate from a double submit.
 */
const trackAdsAction = (action, params = {}) => {
  if (!hasConsent()) return;
  tellGoogle("event", "conversion", {
    send_to: `${ADS_ACCOUNT}/${ADS_ACTIONS[action]}`,
    ...params,
  });
};

/**
 * Report a conversion to Google Ads, only ever with consent.
 *
 * `transaction_id` is the quote or contact reference. Google uses it to discard
 * duplicates, so a double submit or a retry can never be counted twice.
 */
const trackGoogleConversion = (params) => {
  if (!hasConsent()) return;
  tellGoogle("event", GOOGLE_CONVERSION_EVENT, params);
};

/** A page view for an in-app route change. */
export const trackPageView = () => track("PageView");

/**
 * Somebody finished a quote. Sent as a Lead so ad spend can be measured
 * against real enquiries, with the quoted figure as the value.
 */
export const trackQuoteCompleted = (result) => {
  const quote = result?.quote || {};
  const headline = (quote.totals || []).find((total) => total.emphasis);
  const value = Number(headline?.amount ?? 0);
  const label = result?.headline || quote.service_label || "Quote completed";

  track("Lead", {
    content_name: label,
    content_category: result?.service || quote.service || "quote",
    value,
    currency: "ZAR",
  });

  // Google Ads gets the same conversion, carrying the quoted figure so the
  // spend can be judged on the value of the work, not just the count.
  trackGoogleConversion({
    value,
    currency: "ZAR",
    transaction_id: result?.quote_ref || undefined,
    event_category: "quote_completed",
    event_label: label,
  });

  // ...and against the "quote completed" conversion action.
  trackAdsAction("quoteCompleted", {
    value,
    currency: "ZAR",
    transaction_id: result?.quote_ref || undefined,
  });
};

/** Somebody asked us to contact them instead of taking a quote. */
export const trackContactRequest = (service, reference) => {
  track("Contact", {
    content_name: "Contact request",
    content_category: service || "not_covered",
  });

  // No value is sent: the work hasn't been priced yet, and a R0 conversion
  // would drag down the average value Google reports for the others.
  trackGoogleConversion({
    currency: "ZAR",
    transaction_id: reference || undefined,
    event_category: "contact_request",
    event_label: service || "not_covered",
  });

  trackAdsAction("contactRequest", {
    transaction_id: reference || undefined,
  });
};

/**
 * Somebody finished the scoping brief on the outsourced financial management
 * page. No value either: the fee is agreed after the conversation, not here.
 */
export const trackFinanceEnquiry = (reference) => {
  track("Contact", {
    content_name: "Finance function enquiry",
    content_category: "outsourced_financial_management",
  });

  trackGoogleConversion({
    currency: "ZAR",
    transaction_id: reference || undefined,
    event_category: "finance_function_enquiry",
    event_label: "outsourced_financial_management",
  });

  trackAdsAction("financeEnquiry", {
    transaction_id: reference || undefined,
  });
};
